<template>
  <BaseLayout>
    <BaseDashboard
      :is-loading="isDashboardLoading"
    >
      <div
        class="pt-8 pb-2 px-6"
      >
        <section
          class="mb-6"
        >
          <BaseHelpTooltip>
            <template v-slot:left>
              <div>
                <h1 class="page-title font-weight-bold">
                  Reports
                </h1>
              </div>
            </template>
            <template v-slot:popover-body>
              <p>The reports dashboard allows you to generate reports from all campaigns that have reporting data against it. You can select multiple reports for download.</p>
            </template>
          </BaseHelpTooltip>
        </section>
        <section
          :key="reportGeneratorKey"
        >
          <b-card
            no-body
          >
            <b-card-body
              class="py-6 px-7 mb-0"
            >
              <section>
                <reports-generator-filters
                  @addReport="addReport"
                />
              </section>
              <hr>
              <section>
                <div
                  v-for="(report, index) in reportsList"
                  :key="index"
                  class="d-flex mb-4 report align-items-center"
                >
                  <span class="d-flex">
                    <p>{{ report.campaign.name }}</p>
                    <p>{{ selectedReportType }}</p>
                    <p>{{ getFilterValue('publisher', report) }}</p>
                    <p>{{ getFilterValue('creative', report) }}</p>
                  </span>
                  <b-button
                    size="sm"
                    variant="danger"
                    class="ml-auto"
                    @click="removeReport(index)"
                  >
                    <i class="fa-light fa-trash-can" />
                  </b-button>
                </div>
              </section>
              <section
                class="d-flex reports-controls"
              >
                <div
                  class="ml-auto"
                >
                  <b-button
                    class="mr-4"
                    variant="tertiary"
                    @click="openResetFiltersModal()"
                  >
                    Reset
                  </b-button>
                  <b-button
                    variant="primary"
                    size="md"
                    @click="downloadReportAsCSV()"
                    :disabled="!reportsList.length || isDownloadInProgress"
                    :class="{ 'btn-loading': isDownloadInProgress }"
                  >
                    Download all data as CSV
                  </b-button>
                </div>
              </section>
            </b-card-body>
          </b-card>
        </section>
      </div>
      <b-modal
        id="report-generator-reset"
        ref="report-generator-reset"
        title="Report Filters Reset"
        centered
        @ok="resetReportGenerator()"
        @hidden="closeResetFiltersModal()"
      >
        <p>All filters in the reporting dashboard will reset. Press 'OK' to proceed, or 'Cancel' to cancel reset.</p>
      </b-modal>
    </BaseDashboard>
  </BaseLayout>
</template>
<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as reportGeneratorsTypes from '@/modules/ReportsGenerator/store/types';
import * as rootTypes from '@/store/types';
import textHelper from '@/core/mixins/textHelper';
import fileHelper from '@/core/mixins/fileHelper';

import ReportsGeneratorFilters from '@/modules/ReportsGenerator/components/FiltersSection';

export default {
  name: 'ReportsGenerator',
  mixins: [
    textHelper,
    fileHelper,
  ],
  components: {
    ReportsGeneratorFilters,
  },
  data() {
    return {
      reportGeneratorKey: 0,
      isDashboardLoading: false,
      reportsList: [],
      isDownloadInProgress: false,
    };
  },
  computed: {
    ...mapState('reportsGenerator', [
      'selectedFilters',
      'selectedAdvertiserDetails',
    ]),
    getCSVReportType() {
      return new Map([
        ['Creative Report', 'CAMPAIGN_PERFORMANCE'],
        ['Content Report', 'CONTENT_PERFORMANCE'],
        ['Delivery Report', 'DELIVERY_REPORT'],
      ]).get(this.selectedFilters.reportType) ?? '';
    },
    selectedAdvertiserName() {
      return this.selectedAdvertiserDetails.name ?? '';
    },
    selectedReportType() {
      return this.selectedFilters.reportType ?? '';
    },
    selectedStartDate() {
      return this.selectedFilters.startDate;
    },
    selectedEndDate() {
      return this.selectedFilters.endDate;
    },
  },
  methods: {
    ...mapMutations({
      setGlobalMessage: rootTypes.SET_GLOBAL_MESSAGE,
    }),
    ...mapActions('reporting', [
      reportingTypes.FETCH_OVERALL_CREATIVE_CAMPAIGN_STATS,
      reportingTypes.FETCH_CAMPAIGN_CONTENT_DAILY_STATS,
      reportingTypes.FETCH_CAMPAIGN_PUBLISHER_DAILY_STATS,
    ]),
    ...mapActions('reportsGenerator', [
      reportGeneratorsTypes.DOWNLOAD_REPORT_CSV,
      reportGeneratorsTypes.RESET_STORE,
    ]),
    addReport(report) {
      this.reportsList.push(report);
    },
    removeReport(index) {
      this.reportsList.splice(index, 1);
    },
    getFilterValue(filter, report) {
      return !report[filter].ids ? report[filter].name : `${report[filter].ids.length} ${this.capitalizeFirstLetter(filter)}`;
    },
    openResetFiltersModal() {
      this.$refs['report-generator-reset'].show();
    },
    closeResetFiltersModal() {
      this.$refs['report-generator-reset'].hide();
    },
    async resetReportGenerator() {
      // re-render components.
      await this.RESET_STORE();
      this.reportGeneratorKey++;
      this.reportsList = [];
      this.isDownloadInProgress = false;
    },
    async downloadReportAsCSV() {
      this.isDownloadInProgress = true;
      const { startDate, endDate, advertiserId } = this.selectedFilters;
      const payload = {
        reportType: this.getCSVReportType,
        fromDate: startDate,
        toDate: endDate,
        advertiserId,
        searchParams: [],
      };
      for (let index = 0; index < this.reportsList.length; index++) {
        const searchParam = {
          campaignId: this.reportsList[index].campaign.id,
        };
        if (this.reportsList[index].creative.name !== 'All') searchParam.creativeIds = this.reportsList[index].creative.ids ?? [this.reportsList[index].creative.id];
        if (this.reportsList[index].publisher.name !== 'All') searchParam.publisherIds = this.reportsList[index].publisher.ids ?? [this.reportsList[index].publisher.id];

        payload.searchParams.push(searchParam);
      }

      const csvContent = await this.DOWNLOAD_REPORT_CSV(payload);
      this.isDownloadInProgress = false;
      const fileName = `${this.selectedAdvertiserName} - ${this.selectedReportType} - ${this.selectedStartDate} - ${this.selectedEndDate}`;
      this.downloadCsvFile(csvContent, fileName);
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep {
.report p:not(:last-child) {
  margin-right: 1.5rem;
}
.reports-controls {
  margin-top: 3rem;
}
}
</style>
